import React from 'react';
import { EditorContent, useEditor } from '@tiptap/react';
import { richTextExtensions, richTextHtml, richTextInput, richTextListsInput, serializeRichText } from './richText';
import './RichTextEditor.css';
import { useReadableRichText } from './useReadableRichText';
import { ReadableInk } from './readableInk';

export function RichTextContent({ value, className = '' }: { value: string; className?: string }) {
  const ref = useReadableRichText();
  const html = React.useMemo(() => richTextHtml(value), [value]);
  return <div ref={ref} className={`rich-text-document ${className}`} dangerouslySetInnerHTML={{ __html: html }} />;
}

export default function RichTextEditor({ id, label, value, onChange, readOnly = false, onPaste, placeholder, plainLists = false }: {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  readOnly?: boolean;
  onPaste?: React.ClipboardEventHandler<HTMLDivElement>;
  /** Hint shown while the document is empty, exposed to assistive tech as aria-placeholder. */
  placeholder?: string;
  /** Open a plain value's "- " and "1. " lines as real lists (see plainTextListsHtml). */
  plainLists?: boolean;
}) {
  const toContent = plainLists ? richTextListsInput : richTextInput;
  const lastValue = React.useRef(value);
  const [formattingOpen, setFormattingOpen] = React.useState(false);
  const formattingId = React.useId();
  const [linkOpen, setLinkOpen] = React.useState(false);
  const [linkUrl, setLinkUrl] = React.useState('');
  const [linkError, setLinkError] = React.useState('');
  const editor = useEditor({
    extensions: [...richTextExtensions, ReadableInk],
    content: toContent(value),
    editable: !readOnly,
    shouldRerenderOnTransaction: true,
    // Tiptap spreads `attributes` into the view's own object, so it must stay a plain object.
    // CSS shows `data-placeholder` while the document is a single empty block.
    editorProps: { attributes: {
      id, role: 'textbox', 'aria-label': label, 'aria-multiline': 'true', class: 'rich-text-document',
      ...(placeholder ? { 'aria-placeholder': placeholder, 'data-placeholder': placeholder } : {}),
    } },
    onUpdate: ({ editor: current }) => {
      lastValue.current = serializeRichText(current.getHTML());
      onChange(lastValue.current);
    },
  });

  const ref = useReadableRichText(editor);

  React.useEffect(() => {
    editor?.setEditable(!readOnly, false);
    if (readOnly) setLinkOpen(false);
  }, [editor, readOnly]);
  React.useEffect(() => {
    if (!editor || value === lastValue.current) return;
    lastValue.current = value;
    editor.commands.setContent(toContent(value), { emitUpdate: false });
  }, [editor, value, toContent]);

  const button = (name: string, content: React.ReactNode, action: () => void, active?: boolean, disabled = false) => (
    <button type="button" className="btn xs subtle rich-text-tool" title={name} aria-label={name} aria-pressed={active}
      disabled={disabled} onMouseDown={event => event.preventDefault()} onClick={action}>{content}</button>
  );
  // The list keys. Touch screens keep them on the bar beside the Formatting toggle, as glyphs.
  const listKeys = (touch: boolean) => <>
    {button('Bullet list', '• List', () => editor?.chain().focus().toggleBulletList().run(), editor?.isActive('bulletList'))}
    {button('Numbered list', '1. List', () => editor?.chain().focus().toggleOrderedList().run(), editor?.isActive('orderedList'))}
    {/* ← undoes a block indent before it lifts a list item; → nests an item when it can, otherwise shifts the whole
        list or paragraph (BlockIndent), so a first bullet or a whole selected list can still move right. */}
    {button('Decrease indent', touch ? '⇤' : '← Indent', () => {
      if (editor?.can().outdentBlock()) editor.chain().focus().outdentBlock().run();
      else editor?.chain().focus().liftListItem('listItem').run();
    }, undefined, !editor?.can().outdentBlock() && !editor?.can().liftListItem('listItem'))}
    {button('Increase indent', touch ? '⇥' : 'Indent →', () => {
      if (editor?.can().sinkListItem('listItem')) editor.chain().focus().sinkListItem('listItem').run();
      else editor?.chain().focus().indentBlock().run();
    }, undefined, !editor?.can().sinkListItem('listItem') && !editor?.can().indentBlock())}
    {button('Block quote', '❝', () => editor?.chain().focus().toggleBlockquote().run(), editor?.isActive('blockquote'))}
  </>;
  const applyLink = () => {
    if (!editor || readOnly) return;
    const url = linkUrl.trim();
    if (url && !/^(https?:\/\/|mailto:|tel:)/i.test(url)) { setLinkError('Use an https://, http://, mailto:, or tel: link.'); return; }
    if (url && editor.state.selection.empty && !editor.isActive('link')) {
      editor.chain().focus().insertContent({ type: 'text', text: url, marks: [{ type: 'link', attrs: { href: url } }] }).run();
    } else if (url) editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
    else editor.chain().focus().extendMarkRange('link').unsetLink().run();
    setLinkOpen(false);
  };

  return <div ref={ref} className="rich-text-editor" data-readonly={readOnly || undefined} onPaste={onPaste}>
    {!readOnly && editor ? <>
      <div className="rich-text-quickbar" role="group" aria-label={`${label} lists`}>
        {listKeys(true)}
        <button type="button" className="rich-text-formatting-toggle" aria-label="Formatting" title="Formatting"
          aria-expanded={formattingOpen} aria-controls={formattingId}
          onMouseDown={event => event.preventDefault()}
          onClick={() => setFormattingOpen(open => !open)}>
          <span aria-hidden="true">Aa</span>
          <span aria-hidden="true">{formattingOpen ? '−' : '+'}</span>
        </button>
      </div>
      <div id={formattingId} className="rich-text-formatting" data-expanded={formattingOpen}>
      <div className="rich-text-toolbar" role="group" aria-label={`${label} formatting`}>
        <div className="rich-text-toolbar__group">
          {button('Undo', '↶', () => editor.chain().focus().undo().run(), undefined, !editor.can().undo())}
          {button('Redo', '↷', () => editor.chain().focus().redo().run(), undefined, !editor.can().redo())}
          <select aria-label="Paragraph style" value={[1, 2, 3].find(level => editor.isActive('heading', { level })) ?? 'paragraph'} onChange={event => {
            if (event.target.value === 'paragraph') editor.chain().focus().setParagraph().run();
            else editor.chain().focus().setHeading({ level: Number(event.target.value) as 1 | 2 | 3 }).run();
          }}><option value="paragraph">Normal text</option><option value="1">Heading 1</option><option value="2">Heading 2</option><option value="3">Heading 3</option></select>
          <select aria-label="Font" value={editor.getAttributes('textStyle').fontFamily || ''} onChange={event => { if (event.target.value) editor.chain().focus().setFontFamily(event.target.value).run(); else editor.chain().focus().unsetFontFamily().run(); }}>
            <option value="">Default font</option><option>Arial</option><option>Georgia</option><option>Times New Roman</option><option>Verdana</option><option>Courier New</option>
          </select>
          <select aria-label="Font size" value={editor.getAttributes('textStyle').fontSize || ''} onChange={event => { if (event.target.value) editor.chain().focus().setFontSize(event.target.value).run(); else editor.chain().focus().unsetFontSize().run(); }}>
            <option value="">Size</option>{[10, 11, 12, 14, 16, 18, 24, 32].map(size => <option key={size} value={`${size}pt`}>{size}</option>)}
          </select>
        </div>
        <div className="rich-text-toolbar__group">
          {button('Bold', <b>B</b>, () => editor.chain().focus().toggleBold().run(), editor.isActive('bold'))}
          {button('Italic', <i>I</i>, () => editor.chain().focus().toggleItalic().run(), editor.isActive('italic'))}
          {button('Underline', <u>U</u>, () => editor.chain().focus().toggleUnderline().run(), editor.isActive('underline'))}
          {button('Strikethrough', <s>S</s>, () => editor.chain().focus().toggleStrike().run(), editor.isActive('strike'))}
          <label className="rich-text-color" title="Text color">A<input type="color" aria-label="Text color" defaultValue="#1e293b" onChange={event => editor.chain().focus().setColor(event.target.value).run()} /></label>
          <label className="rich-text-color" title="Highlight color">Highlight<input type="color" aria-label="Highlight color" defaultValue="#fff59d" onChange={event => editor.chain().focus().setBackgroundColor(event.target.value).run()} /></label>
          {button('Clear formatting', 'Clear', () => editor.chain().focus().unsetAllMarks().clearNodes().unsetTextAlign().run())}
        </div>
        <div className="rich-text-toolbar__group rich-text-toolbar__group--lists">{listKeys(false)}</div>
        <div className="rich-text-toolbar__group">
          <select aria-label="Text alignment" value={editor.getAttributes('paragraph').textAlign || editor.getAttributes('heading').textAlign || 'left'} onChange={event => editor.chain().focus().setTextAlign(event.target.value).run()}>
            <option value="left">Align left</option><option value="center">Center</option><option value="right">Align right</option><option value="justify">Justify</option>
          </select>
          <select aria-label="Line spacing" value={editor.getAttributes('textStyle').lineHeight || ''} onChange={event => { if (event.target.value) editor.chain().focus().setLineHeight(event.target.value).run(); else editor.chain().focus().unsetLineHeight().run(); }}>
            <option value="">Line spacing</option><option value="1">Single</option><option value="1.5">1.5 lines</option><option value="2">Double</option>
          </select>
          {button('Insert or edit link', 'Link', () => { setLinkUrl(editor.getAttributes('link').href || ''); setLinkError(''); setLinkOpen(true); }, editor.isActive('link'))}
          {button('Remove link', 'Unlink', () => editor.chain().focus().unsetLink().run(), undefined, !editor.isActive('link'))}
        </div>
      </div>
      {linkOpen ? <div className="rich-text-link-editor" role="group" aria-label="Edit link">
        <label>Link URL<input type="url" value={linkUrl} placeholder="https://example.com" onChange={event => { setLinkUrl(event.target.value); setLinkError(''); }} onKeyDown={event => { if (event.key === 'Enter') { event.preventDefault(); applyLink(); } if (event.key === 'Escape') setLinkOpen(false); }} /></label>
        <button className="btn sm" type="button" onClick={applyLink}>Apply link</button><button className="btn-close" type="button" aria-label="Cancel link" onClick={() => setLinkOpen(false)}>×</button>
        {linkError ? <span role="alert">{linkError}</span> : null}
      </div> : null}
      </div>
    </> : null}
    <EditorContent editor={editor} className="rich-text-editor__content" />
  </div>;
}
